import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, Navigate } from "react-router-dom";
import CountrySelect from "../components/CountrySelect.jsx";
import CustomSelect from "../components/CustomSelect.jsx";
import { useAuth } from "../hooks/useAuth.js";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "/v2/api";

const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],
  ["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],["FL","Florida"],["GA","Georgia"],
  ["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],
  ["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],["MD","Maryland"],
  ["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],["MS","Mississippi"],["MO","Missouri"],
  ["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],["NH","New Hampshire"],["NJ","New Jersey"],
  ["NM","New Mexico"],["NY","New York"],["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],
  ["OK","Oklahoma"],["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],["SC","South Carolina"],
  ["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],["VT","Vermont"],
  ["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"],
];

const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "GB", name: "United Kingdom" },
  { code: "AU", name: "Australia" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "BR", name: "Brazil" },
  { code: "IN", name: "India" },
  { code: "MX", name: "Mexico" },
  { code: "NG", name: "Nigeria" },
  { code: "ZA", name: "South Africa" },
  { code: "NZ", name: "New Zealand" },
  { code: "IE", name: "Ireland" },
  { code: "PH", name: "Philippines" },
  { code: "PK", name: "Pakistan" },
];

const Initials = ({ name, size = "lg" }) => {
  const letters = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
  const sizeClass = size === "lg" ? "h-16 w-16 text-2xl" : "h-10 w-10 text-sm";
  return (
    <div className={`flex shrink-0 items-center justify-center rounded-full bg-brand-glow/20 font-semibold text-brand-glow ${sizeClass}`}>
      {letters || "?"}
    </div>
  );
};

const BadgeTooltip = ({ badge, onClose }) => {
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const { progress } = badge;
  const pct = progress ? Math.round((progress.current / progress.target) * 100) : 0;

  return (
    <div
      ref={ref}
      className="absolute z-50 left-0 top-full mt-2 w-64 rounded-2xl border border-white/10 bg-slate-900 p-4 shadow-xl"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-2xl">{badge.icon}</span>
        <p className="font-semibold text-white text-sm">{badge.name}</p>
      </div>
      <p className="text-xs text-white/60 mb-3">{badge.description}</p>
      {progress && !badge.earned && (
        <div>
          <div className="flex justify-between text-xs text-white/40 mb-1">
            <span>Progress</span>
            <span>{progress.current}/{progress.target}</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-white/10">
            <div
              className="h-1.5 rounded-full bg-brand-glow transition-all"
              style={{ width: `${Math.min(pct, 100)}%` }}
            />
          </div>
        </div>
      )}
      {badge.earned && badge.awarded_at && (
        <p className="text-xs text-green-400">
          Earned {new Date(badge.awarded_at).toLocaleDateString()}
        </p>
      )}
    </div>
  );
};

const BadgeCard = ({ badge }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center gap-3 rounded-2xl border px-3 py-2 text-left transition
          ${badge.earned
            ? "border-white/10 bg-white/5"
            : "border-white/5 bg-white/[0.02] opacity-50 grayscale"
          }`}
      >
        <span className="text-2xl">{badge.icon}</span>
        <div className="min-w-0 flex-1">
          <p className={`truncate text-sm font-semibold ${badge.earned ? "text-white" : "text-white/50"}`}>
            {badge.name}
          </p>
          {badge.earned ? (
            <p className="text-xs text-green-400/80">Earned</p>
          ) : badge.progress ? (
            <p className="text-xs text-white/30">
              {badge.progress.current}/{badge.progress.target}
            </p>
          ) : null}
        </div>
      </button>
      {open && <BadgeTooltip badge={badge} onClose={() => setOpen(false)} />}
    </div>
  );
};

const BadgesCard = ({ badges }) => (
  <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
    <h2 className="text-sm font-semibold uppercase tracking-widest text-white/50">Badges</h2>
    {badges === null ? (
      <div className="mt-4 h-10 animate-pulse rounded-xl bg-white/5" />
    ) : (
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {badges.map((b) => (
          <BadgeCard key={b.slug} badge={b} />
        ))}
      </div>
    )}
  </div>
);

const ProfilePage = () => {
  const { user, ready, authFetch } = useAuth();

  const [fields, setFields] = useState({ display_name: "", country: "", state: "" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [badges, setBadges] = useState(null); // null = loading

  // Populate form from current user once loaded
  useEffect(() => {
    if (user) {
      setFields({
        display_name: user.display_name || "",
        country: user.country || "",
        state: user.state || "",
      });
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    authFetch(`${API_BASE}/auth/me/badges/`)
      .then((r) => r.ok ? r.json() : [])
      .then(setBadges)
      .catch(() => setBadges([]));
  }, [user]);

  // Clear state when country changes away from US
  const handleCountryChange = (e) => {
    const val = e.target.value;
    setFields((prev) => ({ ...prev, country: val, state: val === "US" ? prev.state : "" }));
  };

  // Wait for session restore before deciding to redirect — otherwise a hard
  // refresh at /profile kicks authenticated users to /login before the stored
  // refresh token has been exchanged for a new access token.
  if (!ready) return <div className="h-64 animate-pulse rounded-3xl bg-white/5" />;
  if (!user) return <Navigate to="/login" replace />;

  const handleChange = (e) => {
    setFields((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const r = await authFetch(`${API_BASE}/auth/me/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fields),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        const first = Object.values(err)[0];
        throw new Error(Array.isArray(first) ? first[0] : (err.detail || "Save failed."));
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const leaderboardHref = fields.country
    ? `/leaderboard?country=${fields.country}${fields.country === "US" && fields.state ? `&state=${fields.state}` : ""}`
    : "/leaderboard";

  return (
    <div className="mx-auto max-w-lg space-y-8 pt-4">
      <Helmet>
        <title>Your Profile — QuizFreak</title>
        <meta name="description" content="Manage your QuizFreak profile, update your display name and country, and track your badges." />
        <link rel="canonical" href="https://quizfreak.com/v2/profile/" />
      </Helmet>
      {/* Header */}
      <div className="flex items-center gap-4">
        <Initials name={user.username} size="lg" />
        <div>
          <h1 className="text-2xl font-semibold text-white">{user.username}</h1>
          <p className="mt-1 text-sm font-semibold text-brand-glow">
            {user.total_points?.toLocaleString()} pts
          </p>
        </div>
      </div>

      {/* Edit form */}
      <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-white/50">
          Edit profile
        </h2>
        <form onSubmit={handleSave} className="mt-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/80" htmlFor="display_name">
              Display name
            </label>
            <input
              id="display_name"
              name="display_name"
              type="text"
              value={fields.display_name}
              onChange={handleChange}
              placeholder={user.username}
              className="mt-1 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/20 focus:border-brand-glow/60 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/80" htmlFor="country">
              Country
            </label>
            <div className="mt-1">
              <CountrySelect
                value={fields.country}
                onChange={(val) => handleCountryChange({ target: { value: val } })}
                options={COUNTRIES.map((c) => ({ value: c.code, label: c.name }))}
                placeholder="— not set —"
                variant="form"
              />
            </div>
          </div>

          {fields.country === "US" && (
            <div>
              <label className="block text-sm font-medium text-white/80" htmlFor="state">
                State
              </label>
              <div className="mt-1">
                <CustomSelect
                  value={fields.state}
                  onChange={(val) => handleChange({ target: { name: "state", value: val } })}
                  options={US_STATES.map(([code, name]) => ({ value: code, label: name }))}
                  placeholder="— not set —"
                  variant="form"
                />
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-300">{error}</p>}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={saving}
              className="rounded-full bg-brand-glow px-5 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
            {saved && <span className="text-sm text-green-400">Saved!</span>}
          </div>
        </form>
      </div>

      {/* Leaderboard link */}
      <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-4">
        <p className="text-sm text-white/60">
          See how you stack up —{" "}
          <Link to={leaderboardHref} className="font-semibold text-brand-glow hover:underline">
            view {fields.country === "US" && fields.state
              ? `${fields.state}, US`
              : fields.country === "US"
              ? "United States"
              : fields.country
              ? COUNTRIES.find((c) => c.code === fields.country)?.name ?? fields.country
              : "global"} leaderboard
          </Link>
        </p>
      </div>

      {/* Badges */}
      <BadgesCard badges={badges} />
    </div>
  );
};

export default ProfilePage;
